import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Atom, Plus, Sun, Moon, Monitor, UserRound, Headphones, Home, Bell, ArrowLeft, Cloud, Tag, MessageSquare, ChevronDown, ChevronLeft, MoreVertical, Check, Mail, LogOut, UserPlus, Trash2, ShieldCheck, FileText } from "lucide-react";

import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { ImpressiveTooltip } from "../ui/impressive-tooltip";
import { NotificationCenter } from "../notifications/NotificationCenter";
import { NOTIFICATIONS_CHANGED_EVENT } from "../../lib/push";
import { FeedbackDialog } from "../feedback/FeedbackDialog";
import { HEADER_GRADIENT } from "../../constants/theme";
import { useTheme } from "./theme-context";
import ApiService from "../../services/api-service";
import inspireLogo from "../../images/deepam.png";
import { isNativeApp } from "../../lib/native";

interface HeaderProps {
  currentUser?: { id?: number; username?: string; picture?: string; firstName?: string; name?: string; mode?: string; email?: string } | null;
  onOpenAuth: (mode?: string) => void;
  onOpenCreateModal?: () => void;
  isSubscribed?: boolean | null;
  onSubscribe?: () => void;
  onUnsubscribe?: () => void;
  isSubscriptionUpdating?: boolean;
}

export type InspireStackLogoProps = {
  size?: "sm" | "md" | "lg" | "xl";
  width?: number;
  height?: number;
  rounded?: boolean;
  alt?: string;
};

export const InspireStackLogo = ({
  size = "md",
  width,
  height,
  rounded = true,
  alt = "The Deepam Logo",
}: InspireStackLogoProps) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const inlineStyle =
    width || height
      ? {
          width: width ? `${width}px` : undefined,
          height: height ? `${height}px` : undefined,
        }
      : undefined;

  return (
     <img
      src={inspireLogo}
      alt={alt}
      width={width || 50}
      height={height || 50}
      className={rounded ? "rounded-md" : ""}
    />  
  );
};

function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <DropdownMenu>
      <ImpressiveTooltip
        label="Theme switcher"
        description={theme === "system" ? "Following your system preference." : `Currently in ${theme} mode.`}
        icon={<Sun className="w-3.5 h-3.5" />}
        side="bottom"
        align="center"
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="w-9 h-9 p-0">
            <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
            <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            <span className="sr-only">Toggle theme</span>
          </Button>
        </DropdownMenuTrigger>
      </ImpressiveTooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setTheme("light")}>
          <Sun className="mr-2 h-4 w-4" />
          <span>Light</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          <Moon className="mr-2 h-4 w-4" />
          <span>Dark</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")}>
          <Monitor className="mr-2 h-4 w-4" />
          <span>System</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const lightHeaderGradient = "linear-gradient(135deg, #f8fafc 0%, #edf2f7 60%, #e2e8f0 100%)";

export function Header({
  currentUser,
  onOpenAuth,
  onOpenCreateModal = () => {},
  isSubscribed = null,
  onSubscribe = () => {},
  onUnsubscribe = () => {},
  isSubscriptionUpdating = false,
}: HeaderProps) {
  const { theme } = useTheme();
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  // A nav button for the page you're already on is dead weight - it's
  // hidden there and still shown everywhere else, rather than just
  // highlighted active.
  const isAuthorsPage = location.pathname === "/quotes/authors";
  const isTagsPage = location.pathname === "/tag-cloud";
  const navigate = useNavigate();
  // location.key is "default" on the first page of a visit (e.g. a shared
  // link opened in a new tab) - there's no in-app page to go back to, so
  // Back goes home instead of leaving the site.
  const handleBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };
  const [appliedTheme, setAppliedTheme] = useState<"light" | "dark">("dark");
  const [isFriendPanelOpen, setIsFriendPanelOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  // Unread notifications (follows, new posts, likes, comments, replies,
  // mentions) - not the evergreen "suggested for you" list, which is always
  // non-empty and would otherwise light up the bell permanently.
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const resolveTheme = () => {
      if (theme === "dark" || theme === "light") return theme;
      return root.classList.contains("dark") ? "dark" : "light";
    };
    setAppliedTheme(resolveTheme());
  }, [theme]);

  useEffect(() => {
    if (!currentUser?.id) {
      setUnreadCount(0);
      return;
    }

    let cancelled = false;
    const fetchUnreadCount = () => {
      ApiService.getUnreadNotificationCount()
        .then((response: { count?: number }) => {
          if (!cancelled) setUnreadCount(response?.count || 0);
        })
        .catch(() => {});
    };

    // A new follow happens on someone else's session, so this tab has no way
    // to know about it - poll periodically, and refresh immediately whenever
    // the tab regains focus, instead of only fetching once at login/mount.
    fetchUnreadCount();
    const intervalId = window.setInterval(fetchUnreadCount, 30000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchUnreadCount();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", fetchUnreadCount);
    // A push arrived, or a notification was read or deleted.
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, fetchUnreadCount);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", fetchUnreadCount);
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, fetchUnreadCount);
    };
  }, [currentUser?.id]);

  if (currentUser) {
    localStorage.setItem("inspirelens-theme", currentUser.mode || "system");
  }

  const isDarkMode = appliedTheme === "dark";
  const textColor = isDarkMode ? "text-white" : "text-slate-900";
  const subTextColor = isDarkMode ? "text-slate-200" : "text-slate-500";
  const buttonTextColor = isDarkMode ? "text-white" : "text-slate-900";
  const loginHover = isDarkMode ? "hover:bg-slate-700" : "hover:bg-slate-100";
  const signupClass = isDarkMode
    ? "bg-white text-slate-900 hover:bg-slate-100"
    : "bg-slate-900 text-white hover:bg-slate-800";

  // Icon-above-label nav buttons. Each one is hidden on the page it links
  // to (see isHomePage/isAuthorsPage/isTagsPage below), so none of these
  // are ever shown "active" - no active-state color needed.
  const navItemClass =
    "flex h-auto min-h-9 flex-col items-center justify-center gap-0.5 rounded px-2 py-1.5 sm:px-3 transition-colors text-white hover:bg-slate-700 dark:hover:bg-slate-800";

  if (isNativeApp) {
    return (
      <NativeAppBar
        currentUser={currentUser}
        onOpenAuth={onOpenAuth}
        isHomePage={isHomePage}
        onBack={handleBack}
        unreadCount={unreadCount}
        onOpenNotifications={() => setIsFriendPanelOpen(true)}
        onOpenFeedback={() => setIsFeedbackOpen(true)}
        isSubscribed={isSubscribed}
        onSubscribe={onSubscribe}
        onUnsubscribe={onUnsubscribe}
        isSubscriptionUpdating={isSubscriptionUpdating}
      >
        <FeedbackDialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen} defaultEmail={currentUser?.email} />
        <NotificationCenter open={isFriendPanelOpen} onClose={() => setIsFriendPanelOpen(false)} />
      </NativeAppBar>
    );
  }

  return (
    <>
    <div className="text-white rounded-2xl shadow-lg border border-white/10" style={{ backgroundImage: HEADER_GRADIENT }}>
      <div className="px-3 py-3 sm:px-4 sm:py-4">
        {/* flex-wrap so the nav drops to its own row instead of crushing the
            logo to zero width when there isn't room for both on one line
            (the nav side has shrink-0, so without this the logo side - the
            only one allowed to shrink - would be squeezed to nothing). */}
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-3">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="min-w-0"
          >
            <Link
              to="/"
              aria-label="Go to homepage"
              className="flex items-center gap-2 sm:gap-3 rounded-lg transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 min-w-0"
            >
              <div className="shrink-0 [&_img]:w-8 [&_img]:h-8 sm:[&_img]:w-10 sm:[&_img]:h-10">
                <InspireStackLogo />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-semibold text-white flex items-center gap-1.5 sm:gap-2 truncate">
                  The Deepam
                  <span className="inline-flex text-gradient mt-auto items-center text-[#fc991d] text-xs sm:text-sm font-medium shrink-0">BETA</span>
                </h1>
                <p className="hidden sm:block mt-1 text-slate-200 dark:text-slate-300 text-sm font-medium truncate">
                  Discover • Share • Grow Smarter Together
                </p>
              </div>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            // grow + min-w-0 (instead of a viewport breakpoint like xl:) so
            // this claims whatever space is left on its line regardless of
            // how wide the *page's own* container is (Header renders inside
            // max-w-4xl/6xl/7xl wrappers that differ per page - a
            // viewport-based breakpoint can't see that, only flex-grow can).
            // That both keeps it beside the logo when there's room and, once
            // it wraps onto its own row (see the outer flex-wrap), gives it
            // the full row width so its own buttons wrap instead of
            // overflowing.
            className="flex min-w-0 grow flex-row flex-wrap items-center justify-end gap-1 sm:gap-2"
          >
            {!isHomePage && (
              <Button asChild variant="ghost" size="sm" aria-label="Home" className={navItemClass}>
                <Link to="/" className="flex flex-col items-center justify-center gap-0.5">
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline text-[11px] font-medium">Home</span>
                </Link>
              </Button>
            )}

            {!isAuthorsPage && (
              <Button asChild variant="ghost" size="sm" aria-label="Author cloud" className={navItemClass}>
                <Link to="/quotes/authors" className="flex flex-col items-center justify-center gap-0.5">
                  <Cloud className="w-4 h-4" />
                  <span className="hidden sm:inline text-[11px] font-medium">Authors</span>
                </Link>
              </Button>
            )}

            {!isTagsPage && (
              <Button asChild variant="ghost" size="sm" aria-label="Tag cloud" className={navItemClass}>
                <Link to="/tag-cloud" className="flex flex-col items-center justify-center gap-0.5">
                  <Tag className="w-4 h-4" />
                  <span className="hidden sm:inline text-[11px] font-medium">Tags</span>
                </Link>
              </Button>
            )}

            <Button
              variant="ghost"
              size="sm"
              aria-label="Send feedback"
              onClick={() => setIsFeedbackOpen(true)}
              className={navItemClass}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline text-[11px] font-medium">Feedback</span>
            </Button>
            <FeedbackDialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen} defaultEmail={currentUser?.email} />

            {/* Listen feature disabled - uncomment (with the /listen routes in App.tsx) to re-enable.
            <Button
              asChild
              variant="ghost"
              size="sm"
              aria-label="Listen"
              className="text-white hover:bg-slate-700 dark:hover:bg-slate-800 rounded w-9 h-9 p-0 sm:w-auto sm:h-auto sm:px-3 sm:py-2"
            >
              <Link to="/listen" className="flex items-center justify-center sm:gap-1.5">
                <Headphones className="w-4 h-4" />
                <span className="hidden sm:inline">Listen</span>
              </Link>
            </Button>
            */}

            <ThemeToggle />

            {currentUser && (
              <ImpressiveTooltip
                label="Notifications"
                description="See who followed you and who to follow next."
                icon={<Bell className="w-3.5 h-3.5" />}
                side="bottom"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative w-9 h-9 p-0 text-white hover:bg-slate-700 dark:hover:bg-slate-800"
                  onClick={() => setIsFriendPanelOpen(true)}
                  aria-label="Friend recommendations"
                >
                  <Bell className="h-4 w-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#fc991d] px-1 text-[10px] font-semibold leading-none text-white">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Button>
              </ImpressiveTooltip>
            )}

            {currentUser ? (
              <ImpressiveTooltip
                label="Add inspiration"
                description="Share something new with The Deepam."
                icon={<Plus className="w-3.5 h-3.5" />}
                side="bottom"
              >
                <Button
                  onClick={onOpenCreateModal}
                  size="sm"
                  aria-label="Add"
                  className="bg-[#fc991d] text-white hover:bg-[#e8890a] rounded w-9 h-9 p-0 sm:w-auto sm:h-auto sm:px-4 sm:py-2"
                >
                  <Plus className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Add</span>
                  <ChevronDown className="hidden sm:inline w-3.5 h-3.5 sm:ml-1 opacity-80" aria-hidden="true" />
                </Button>
              </ImpressiveTooltip>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onOpenAuth("login")}
                  className="text-white hover:bg-slate-700 dark:hover:bg-slate-800 rounded px-2.5 sm:px-3"
                >
                  Log in
                </Button>
                <Button
                  size="sm"
                  onClick={() => onOpenAuth("signup")}
                  className="bg-white text-slate-800 hover:bg-slate-100 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 rounded px-2.5 sm:px-3"
                >
                  Sign up
                </Button>
              </>
            )}

            {currentUser && (
              <DropdownMenu>
                <ImpressiveTooltip
                  label="Profile & settings"
                  description="Manage subscriptions, theme, and sign out."
                  icon={<UserRound className="w-3.5 h-3.5" />}
                  side="bottom"
                  align="end"
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Profile & settings"
                      className="text-white hover:bg-slate-700 dark:hover:bg-slate-800 rounded-full h-9 px-1 sm:pr-1.5 flex items-center gap-1"
                    >
                      {currentUser.picture ? (
                        <img
                          src={currentUser.picture}
                          alt={currentUser.firstName || currentUser.name}
                          className="w-7 h-7 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-slate-600 dark:bg-slate-500 grid place-items-center text-white font-medium text-xs">
                          {((currentUser.firstName || currentUser.name || "").slice(0, 2)).toUpperCase()}
                        </div>
                      )}
                      <ChevronDown className="hidden sm:inline w-3.5 h-3.5 opacity-80" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                </ImpressiveTooltip>
                <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg rounded-lg">
                  {currentUser.username && currentUser.id && (
                    <>
                      <DropdownMenuItem asChild className="focus:bg-slate-100 dark:focus:bg-slate-700 rounded">
                        <Link to={`/user/${currentUser.username}/${currentUser.id}?view=mine`}>My Profile</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="focus:bg-slate-100 dark:focus:bg-slate-700 rounded">
                        <Link to="/settings/notifications">Notification settings</Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-700" />
                    </>
                  )}
                  {typeof isSubscribed === "boolean" ? (
                    <DropdownMenuItem
                      onClick={isSubscribed ? onUnsubscribe : onSubscribe}
                      className="focus:bg-slate-100 dark:focus:bg-slate-700 rounded"
                      disabled={isSubscriptionUpdating}
                    >
                      {isSubscriptionUpdating ? "Updating..." : isSubscribed ? "Unsubscribe" : "Subscribe"}
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem disabled className="text-slate-400 dark:text-slate-500">
                      Loading preference...
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-700" />
                  {/* Listen feature disabled.
                  <DropdownMenuItem asChild className="focus:bg-slate-100 dark:focus:bg-slate-700 rounded">
                    <Link to="/admin/listen">Listen Admin</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-700" />
                  */}
                  <DropdownMenuItem onClick={() => onOpenAuth("logout")} className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900 rounded">
                    Log out
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onOpenAuth("delete-account")} className="text-slate-500 focus:text-red-600 focus:bg-red-50 dark:text-slate-400 dark:focus:bg-red-900 rounded">
                    Delete account
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </motion.div>
        </div>
      </div>
    </div>

    {/* Back sits just below the header bar, left-aligned, on every page
        except the home page. */}
    {!isHomePage && (
      <div className="mt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          aria-label="Back to previous page"
          className="gap-2 px-3 text-slate-900 hover:bg-slate-200/70 hover:text-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </Button>
      </div>
    )}

    <NotificationCenter open={isFriendPanelOpen} onClose={() => setIsFriendPanelOpen(false)} />
    </>
  );
}

interface NativeAppBarProps {
  currentUser: HeaderProps["currentUser"];
  onOpenAuth: (mode?: string) => void;
  isHomePage: boolean;
  onBack: () => void;
  unreadCount: number;
  onOpenNotifications: () => void;
  onOpenFeedback: () => void;
  isSubscribed: boolean | null;
  onSubscribe: () => void;
  onUnsubscribe: () => void;
  isSubscriptionUpdating: boolean;
  children?: React.ReactNode;
}

// The native app's top bar: one compact, pinned row (logo, back on inner
// pages, notifications, a single menu). Home/Authors/Liked/Profile/Add live in
// MobileTabBar, so the website header's nav buttons aren't repeated here.
// Rendered into <body> so `fixed` isn't trapped by a transformed page wrapper;
// the spacer keeps page content from sliding under it.
function NativeAppBar({
  currentUser,
  onOpenAuth,
  isHomePage,
  onBack,
  unreadCount,
  onOpenNotifications,
  onOpenFeedback,
  isSubscribed,
  onSubscribe,
  onUnsubscribe,
  isSubscriptionUpdating,
  children,
}: NativeAppBarProps) {
  const { theme, setTheme } = useTheme();
  const menuItemClass = "min-h-11 rounded-lg focus:bg-slate-100 dark:focus:bg-slate-700";
  const themeOptions = [
    { value: "light", label: "Light", Icon: Sun },
    { value: "dark", label: "Dark", Icon: Moon },
    { value: "system", label: "System", Icon: Monitor },
  ] as const;

  const bar = (
    <header className="fixed inset-x-0 top-0 z-40 bg-[#14213d] pt-[env(safe-area-inset-top)] text-white shadow-md shadow-slate-900/20">
      <div className="flex h-14 items-center gap-1 px-2">
        {!isHomePage && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full active:bg-white/10"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <Link
          to="/"
          aria-label="The Deepam home"
          className={`flex min-w-0 items-center gap-2.5 rounded-lg ${isHomePage ? "pl-2" : ""}`}
        >
          <img src={inspireLogo} alt="" className="h-8 w-8 shrink-0 rounded-lg bg-white object-cover" />
          <span className="truncate text-[17px] font-semibold tracking-tight">The Deepam</span>
        </Link>

        <div className="ml-auto flex shrink-0 items-center gap-1">
          {currentUser ? (
            <button
              type="button"
              onClick={onOpenNotifications}
              aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} new` : "Notifications"}
              className="relative grid h-11 w-11 place-items-center rounded-full active:bg-white/10"
            >
              <Bell className="h-[22px] w-[22px]" />
              {unreadCount > 0 && (
                <span className="absolute right-2 top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#fc991d] px-1 text-[10px] font-bold leading-none text-slate-900">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onOpenAuth("login")}
              className="h-9 rounded-full bg-[#fc991d] px-4 text-sm font-semibold text-slate-900 active:bg-[#e8890a]"
            >
              Sign in
            </button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={currentUser ? "Account and settings" : "Settings"}
                className="grid h-11 w-11 place-items-center rounded-full active:bg-white/10"
              >
                {currentUser ? (
                  currentUser.picture ? (
                    <img src={currentUser.picture} alt="" className="h-8 w-8 rounded-full object-cover ring-2 ring-white/20" />
                  ) : (
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-slate-600 text-xs font-semibold">
                      {(currentUser.firstName || currentUser.name || "").slice(0, 2).toUpperCase()}
                    </span>
                  )
                ) : (
                  <MoreVertical className="h-[22px] w-[22px]" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 rounded-xl p-1.5 dark:bg-slate-800">
              {currentUser?.username && currentUser.id && (
                <DropdownMenuItem asChild className={menuItemClass}>
                  <Link to={`/user/${currentUser.username}/${currentUser.id}?view=mine`}>
                    <UserRound className="mr-2 h-4 w-4" /> My profile
                  </Link>
                </DropdownMenuItem>
              )}
              {currentUser && (
                <DropdownMenuItem asChild className={menuItemClass}>
                  <Link to="/settings/notifications">
                    <Bell className="mr-2 h-4 w-4" /> Notification settings
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild className={menuItemClass}>
                <Link to="/tag-cloud">
                  <Tag className="mr-2 h-4 w-4" /> Browse tags
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenFeedback} className={menuItemClass}>
                <MessageSquare className="mr-2 h-4 w-4" /> Send feedback
              </DropdownMenuItem>
              <DropdownMenuItem asChild className={menuItemClass}>
                <Link to="/privacy">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Privacy policy
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className={menuItemClass}>
                <Link to="/terms">
                  <FileText className="mr-2 h-4 w-4" /> Terms of service
                </Link>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-700" />
              <p className="px-2 pb-1 pt-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">Appearance</p>
              {themeOptions.map(({ value, label, Icon }) => (
                <DropdownMenuItem key={value} onClick={() => setTheme(value)} className={menuItemClass}>
                  <Icon className="mr-2 h-4 w-4" /> {label}
                  {theme === value && <Check className="ml-auto h-4 w-4" aria-label="selected" />}
                </DropdownMenuItem>
              ))}

              <DropdownMenuSeparator className="my-1 bg-slate-100 dark:bg-slate-700" />
              {currentUser ? (
                <>
                  {typeof isSubscribed === "boolean" && (
                    <DropdownMenuItem
                      onClick={isSubscribed ? onUnsubscribe : onSubscribe}
                      disabled={isSubscriptionUpdating}
                      className={menuItemClass}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      {isSubscriptionUpdating ? "Updating..." : isSubscribed ? "Stop daily digest" : "Get daily digest"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => onOpenAuth("logout")}
                    className={`${menuItemClass} text-red-600 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-900`}
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onOpenAuth("delete-account")}
                    className={`${menuItemClass} text-slate-500 focus:bg-red-50 focus:text-red-600 dark:text-slate-400 dark:focus:bg-red-900`}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete account
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onClick={() => onOpenAuth("signup")} className={menuItemClass}>
                  <UserPlus className="mr-2 h-4 w-4" /> Create an account
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );

  return (
    <>
      {typeof document !== "undefined" ? createPortal(bar, document.body) : bar}
      <div aria-hidden="true" className="h-14" />
      {children}
    </>
  );
}
